import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LOGO_WHITE } from "@/lib/assets";
import WaveField from "@/components/site/WaveField";
import Impressum from "@/components/site/Impressum";
import PrivacyPolicy from "@/components/site/PrivacyPolicy";

const EMAILS = ["info@akkasmusic.com", "akkas@akkasmusic.com"];
const PHONE = "+49 177 77 424 73";
const PHONE_HREF = "tel:+4917777442473";

const SOCIALS = [
  { name: "Instagram", id: "instagram", url: "https://www.instagram.com/akkasmusic" },
  {
    name: "LinkedIn",
    id: "linkedin",
    url: "https://www.linkedin.com/in/s%C3%BCleyman-akkas-55a895116/",
  },
];

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="px-6 lg:px-14 py-28 lg:py-44 bg-[#121110] text-[#FBF9F5] relative isolate overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[60%] -z-10 opacity-35">
        <WaveField color="251,249,245" lines={40} />
      </div>

      <p className="font-mono-accent text-[11px] uppercase tracking-[0.4em] text-[#C5A880] mb-12">
        05 / For brands that value taste, depth and distinction
      </p>

      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="font-serif-display font-light tracking-tight leading-[0.98] text-[11vw] sm:text-[8vw] lg:text-[6vw] max-w-[16ch]"
      >
        Let's create something <span className="italic text-[#C5A880]">worth hearing.</span>
      </motion.h2>

      <div className="mt-16 lg:mt-24 grid lg:grid-cols-2 gap-14 lg:gap-20 border-t border-white/15 pt-14">
        <div>
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 block mb-5">
            Direct
          </span>
          <div className="flex flex-col gap-4">
            {EMAILS.map((email, i) => (
              <a
                key={email}
                data-testid={i === 0 ? "contact-email-primary" : "contact-email-secondary"}
                href={`mailto:${email}`}
                className="group flex items-center gap-4"
              >
                <span
                  className="font-serif-display font-light text-2xl sm:text-3xl lg:text-4xl link-underline pb-1"
                  style={{ backgroundImage: "linear-gradient(#FBF9F5,#FBF9F5)" }}
                >
                  {email}
                </span>
                <ArrowUpRight className="h-5 w-5 text-white/40 transition-colors duration-500 group-hover:text-[#C5A880]" strokeWidth={1.25} />
              </a>
            ))}
            <a
              data-testid="contact-phone"
              href={PHONE_HREF}
              className="font-serif-display font-light text-2xl sm:text-3xl text-white/80 link-underline pb-1 w-fit mt-1"
              style={{ backgroundImage: "linear-gradient(#FBF9F5,#FBF9F5)" }}
            >
              {PHONE}
            </a>
          </div>
          <p className="mt-6 text-white/50 text-sm font-light max-w-md leading-relaxed">
            Selected references are shared personally, upon request. Enquiries welcome from anywhere
            in the world.
          </p>
        </div>

        <div>
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 block mb-5">
            Elsewhere
          </span>
          <ul className="flex flex-col divide-y divide-white/12 border-y border-white/12">
            {SOCIALS.map((s) => (
              <li key={s.id}>
                <a
                  data-testid={`social-link-${s.id}`}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between py-5"
                >
                  <span className="font-serif-display font-light text-2xl sm:text-3xl text-[#FBF9F5] transition-transform duration-500 ease-out group-hover:translate-x-2">
                    {s.name}
                  </span>
                  <ArrowUpRight
                    className="h-5 w-5 text-white/40 transition-all duration-500 ease-out group-hover:text-[#C5A880] group-hover:-translate-y-1 group-hover:translate-x-1"
                    strokeWidth={1.25}
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-20 lg:mt-32 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-8 border-t border-white/15 pt-10">
        <div className="flex flex-col gap-4">
          <img src={LOGO_WHITE} alt="Akkas Music" className="h-28 sm:h-36 lg:h-44 w-auto object-contain -ml-1" />
          <div className="flex items-center gap-5">
            <Impressum />
            <span className="h-3 w-px bg-white/25" />
            <PrivacyPolicy />
          </div>
        </div>
        <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/40">
          © {new Date().getFullYear()} — Akkas Music · Sound Branding
        </span>
      </div>
    </section>
  );
}
